import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// PDF.js worker (mesma versão do pdfjs-dist instalado)
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

/**
 * Detecta se o dispositivo é tablet/mobile.
 * Critério: pointer coarse (touch) OU largura de viewport <= 1024px.
 * No PC/Desktop => false (usa iframe legado).
 */
const isTabletOrMobile = () => {
  if (typeof window === 'undefined') return false;
  try {
    const coarse =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(pointer: coarse)').matches;
    const narrow = window.innerWidth <= 1024;
    return coarse || narrow;
  } catch (_e) {
    return false;
  }
};

/**
 * Iframe legado — comportamento inalterado no desktop.
 * Se className for informado (ex.: "pdf-iframe" em VisualizarResumo.css),
 * respeita o CSS existente. Caso contrário, usa estilos padrão que casam
 * com o container atual (altura 75vh, largura 100%).
 */
const IframePdf = ({ url, title, className }) => (
  <iframe
    src={url}
    title={title}
    data-testid="pdf-viewer-iframe"
    className={className || ''}
    style={
      className
        ? undefined
        : {
            width: '100%',
            height: '75vh',
            minHeight: 400,
            border: 'none',
            background: '#ffffff',
          }
    }
  />
);

/**
 * Renderiza o PDF com PDF.js (react-pdf) — usado apenas em tablet/mobile.
 * Se falhar, chama onFail() para que o pai troque para <IframePdf/>.
 * NÃO exibe mensagem de erro antes do fallback.
 */
const TabletPdf = ({ url, title, className, onFail }) => {
  const containerRef = useRef(null);
  const [numPages, setNumPages] = useState(null);
  const [pageWidth, setPageWidth] = useState(null);

  // Observa largura para renderizar página ajustada, com scroll vertical.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const w = Math.max(240, el.clientWidth - 4);
      setPageWidth(w);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('orientationchange', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  const handleLoadSuccess = useCallback(({ numPages: n }) => {
    setNumPages(n);
  }, []);

  const handleLoadError = useCallback(
    (err) => {
      console.warn('[PdfViewer] PDF.js falhou, aplicando fallback iframe:', err);
      if (typeof onFail === 'function') onFail();
    },
    [onFail]
  );

  return (
    <div
      ref={containerRef}
      data-testid="pdf-viewer"
      className={className || ''}
      style={{
        width: '100%',
        maxWidth: '100%',
      }}
    >
      <Document
        file={url}
        onLoadSuccess={handleLoadSuccess}
        onLoadError={handleLoadError}
        onSourceError={handleLoadError}
        loading={
          <div
            data-testid="pdf-viewer-loading"
            style={{ padding: 24, textAlign: 'center', color: '#fff' }}
          >
            Carregando PDF…
          </div>
        }
        // Sem mensagem visível de erro: o pai fará o fallback para iframe.
        error={<span data-testid="pdf-viewer-error-hidden" style={{ display: 'none' }} />}
        noData={<span style={{ display: 'none' }} />}
      >
        {numPages &&
          pageWidth &&
          Array.from({ length: numPages }, (_, i) => (
            <div
              key={`pdf-page-wrap-${i + 1}`}
              style={{
                marginBottom: 12,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Page
                pageNumber={i + 1}
                width={pageWidth}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                onRenderError={handleLoadError}
                loading={
                  <div style={{ padding: 16, color: '#fff', textAlign: 'center' }}>
                    Carregando página {i + 1}…
                  </div>
                }
              />
            </div>
          ))}
      </Document>
    </div>
  );
};

/**
 * PdfViewer — componente único, mesma API pública (props: url, title, className).
 *
 *   Desktop/PC     -> <iframe src={url}/> (comportamento antigo intacto)
 *   Tablet/Mobile  -> PDF.js via react-pdf; se falhar, cai para <iframe/> silenciosamente.
 */
export const PdfViewer = ({ url, title = 'PDF', className = '' }) => {
  const [tablet, setTablet] = useState(() => isTabletOrMobile());
  const [pdfJsFailed, setPdfJsFailed] = useState(false);

  useEffect(() => {
    const handler = () => setTablet(isTabletOrMobile());
    window.addEventListener('resize', handler);
    window.addEventListener('orientationchange', handler);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('orientationchange', handler);
    };
  }, []);

  if (!url) return null;

  // Desktop: sempre iframe. Tablet com falha do PDF.js: iframe (fallback).
  if (!tablet || pdfJsFailed) {
    return <IframePdf url={url} title={title} className={className} />;
  }

  // Tablet/mobile: tenta PDF.js e cai para iframe silenciosamente se falhar.
  return (
    <TabletPdf
      url={url}
      title={title}
      className={className}
      onFail={() => setPdfJsFailed(true)}
    />
  );
};

export default PdfViewer;
